import L from "leaflet";
import { maplibreGL } from "@maplibre/maplibre-gl-leaflet";
import { setWorkerUrl } from "maplibre-gl";
import { layers, namedFlavor } from "@protomaps/basemaps";
import { leafletLayer } from "protomaps-leaflet";
import { API_BASE } from "../api";

// Kartenhintergrund für die Kartenansicht und die Karte im Schülerportal.
//
// Die Kacheln kommen vom eigenen Geocoder über /api/karte, nie von Dritten. Früher lud
// der Browser sie direkt von tile.openstreetmap.org. Das übermittelte die IP-Adresse
// der Nutzer an die OpenStreetMap Foundation in UK, und seit wir keinen Referer mehr
// senden (Referrer-Policy: no-referrer), sperrte OSM die Kacheln für Chrome, Edge und
// Firefox ganz.
//
// Gezeichnet wird mit MapLibre per WebGL2. Fehlt WebGL2, etwa auf virtuellen Desktops
// ohne Grafikbeschleunigung, zeichnet protomaps-leaflet dieselben Kacheln im selben
// Stil per Canvas. Marker, Kreise und Tooltips bleiben in beiden Fällen gewöhnliche
// Leaflet-Layer darüber.

// ODbL verlangt die Nennung von OpenStreetMap.
const ATTRIBUTION = '<a href="https://protomaps.com">Protomaps</a> © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const FLAVOR = "light";
const LANG = "de";
// Der Ausschnitt auf dem Geocoder reicht bis Zoomstufe 15, darüber wird vergrößert.
const MAX_DATA_ZOOM = 15;
// Wie früher beim OSM-TileLayer.
const MAX_ZOOM = 19;

/** MapLibre braucht absolute URLs. API_BASE ist in Produktion leer: gleiche Origin. */
function karteUrl(path: string): string {
  return `${API_BASE || location.origin}/api/karte${path}`;
}

let webgl2Available: boolean | null = null;

/**
 * Prüft einmal je Seitenaufruf, ob der Browser einen WebGL2-Kontext hergibt. MapLibre 6
 * verlangt genau WebGL2, WebGL1 reicht nicht.
 */
function hasWebGL2(): boolean {
  if (webgl2Available === null) {
    try {
      const gl = document.createElement("canvas").getContext("webgl2");
      webgl2Available = !!gl;
      // Sofort freigeben: Browser erlauben nur eine Handvoll Kontexte gleichzeitig.
      gl?.getExtension("WEBGL_lose_context")?.loseContext();
    } catch {
      webgl2Available = false;
    }
  }
  return webgl2Available;
}

let workerUrlSet = false;

/**
 * MapLibre startet einen Web Worker aus einer eigenen Datei, die es sonst über
 * import.meta.url findet. Das gibt es im IIFE-Bundle nicht, deshalb liefert der Build
 * die Datei unter js/vendor/ aus (npm run vendor) und wir nennen sie hier.
 */
function ensureWorkerUrl(): void {
  if (workerUrlSet) return;
  setWorkerUrl(new URL("js/vendor/maplibre-gl-worker.mjs", document.baseURI).href);
  workerUrlSet = true;
}

/**
 * Nimmt einen Layer wieder heraus, dessen onAdd mittendrin gescheitert ist. Ein
 * gewöhnliches removeLayer riefe onRemove auf, und das scheitert an der nie erzeugten
 * MapLibre-Karte. Leaflet meldet die Ereignisse des Layers schon vor onAdd an, ohne
 * das Abmelden liefe bei jedem Verschieben ein Fehler auf.
 */
function detachBrokenLayer(map: L.Map, layer: L.Layer): void {
  const l = layer as any;
  // Leaflet nimmt beim Abmelden per Objekt den Kontext als zweites Argument, die
  // Typdefinition kennt diese Form nicht.
  if (l.getEvents) (map as any).off(l.getEvents(), layer);
  l._container?.remove();
  delete (map as any)._layers[L.Util.stamp(layer)];
  l._map = l._mapToAdd = null;
}

/** Legt den Kartenhintergrund unter eine bestehende Leaflet-Karte. */
export function addBasemap(map: L.Map): void {
  // Bisher kam die Grenze vom TileLayer. Leaflet übernimmt Zoomgrenzen nur von
  // GridLayern, der MapLibre-Layer ist keiner. Ohne feste Grenze wäre sie unendlich, und
  // fitBounds zöge bei einer einzigen Praktikumsstelle ins Bodenlose.
  map.setMaxZoom(MAX_ZOOM);

  if (hasWebGL2()) {
    ensureWorkerUrl();
    const layer = maplibreGL({
      style: {
        version: 8,
        glyphs: karteUrl("/assets/fonts/{fontstack}/{range}.pbf"),
        sprite: karteUrl(`/assets/sprites/v4/${FLAVOR}`),
        sources: {
          protomaps: {
            type: "vector",
            tiles: [karteUrl("/tiles/{z}/{x}/{y}.mvt")],
            maxzoom: MAX_DATA_ZOOM,
            attribution: ATTRIBUTION,
          },
        },
        layers: layers("protomaps", namedFlavor(FLAVOR), { lang: LANG }),
      },
    });
    try {
      layer.addTo(map);
      return;
    } catch {
      // WebGL2 ist da, der Kontext scheitert trotzdem, etwa am Treiber oder weil zu
      // viele Kontexte offen sind. Dann wie ohne WebGL2 weiter.
      console.warn("[Karte] WebGL-Darstellung fehlgeschlagen, zeichne per Canvas.");
      detachBrokenLayer(map, layer);
    }
  }

  leafletLayer({
    url: karteUrl("/tiles/{z}/{x}/{y}.mvt"),
    flavor: FLAVOR,
    lang: LANG,
    maxDataZoom: MAX_DATA_ZOOM,
    attribution: ATTRIBUTION,
  }).addTo(map);
}
